<template>
  <div>
    <gl-tree-view
      :disableActions="true"
      :tree-height="200"
      :entries="itemCategories"
      :selected="categories"
      v-on:update:selected="$emit('update:categories', $event)"
    />
    <div>
      <md-checkbox
        v-model="_excludeOtherCategoryItems"
        :disabled="categories.length === 0"
      >
        選択分類外の商品を表示・集計から除外
      </md-checkbox>
    </div>
    <div class="keyword-search-wrapper">
      <div class="keyword-search">
        <md-field
          md-clearable
          class="keyword-search__keyword-input"
          :class="{ 'md-invalid': noHits }"
        >
          <gl-icon-search />
          <label>検索ワード（コード／名称）</label>
          <md-input ref="text" type="text" :value="text" @input="updateText" />
          <span class="md-error">棚割が見つかりませんでした</span>
        </md-field>
      </div>
    </div>
    <div class="items-container">
      <filter-tanawari-pattern-list
        :items="items"
        :selected.sync="selected"
        selectable
      />
      <div v-if="itemFountExceedsLimit" class="message-max-count-exceeds">
        検索結果上位{{ maxSearchCount }}件を表示しています
      </div>
      <div class="fetching" v-if="fetching">
        <spinner />
      </div>
    </div>
    <div class="button-group">
      <gl-button
        dense
        primary
        icon-pre="list-add"
        class="button-group__button"
        :disabled="items.length === 0"
        @click=";(selected = items), $emit('selected', selected)"
      >
        All
      </gl-button>
      <gl-button
        dense
        primary
        class="button-group__button"
        :disabled="selected.length === 0"
        @click="$emit('selected', selected)"
      >
        <gl-icon-list-add />
      </gl-button>
    </div>
  </div>
</template>

<script>
import { Subject, from, of } from 'rxjs'
import {
  debounceTime,
  distinctUntilChanged,
  tap,
  map,
  switchMap,
} from 'rxjs/operators'
import FilterTanawariPatternList from '@/components/FilterTanawariPatternList.vue'
import Spinner from '@/components/Spinner.vue'

export default {
  components: { FilterTanawariPatternList, Spinner },
  props: {
    /**
     * 企業
     */
    company: {
      type: String,
      required: true,
    },
    dateFrom: {
      type: String,
      required: true,
    },
    dateTo: {
      type: String,
      required: true,
    },
    categories: {
      type: Array,
      default: () => [],
    },
    excludeOtherCategoryItems: {
      type: Boolean,
      default: false,
    },
    /**
     * 検索時、最大で表示する商品数
     *
     * 検索結果（Array）の先頭x件を表示します。並び順はリポジトリがどの
     * 順番で返すかに依存します。
     */
    maxSearchCount: {
      type: Number,
      default: process.env.VUE_APP_MAX_ITEMS_SEARCH_COUNT
        ? Number(process.env.VUE_APP_MAX_ITEMS_SEARCH_COUNT)
        : 100,
    },
    /**
     * POSの開示制限あり
     */
    posRestriction: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      /**
       * 分類マスタ
       */
      itemCategories: [],
      /**
       * 検索テキスト
       */
      text: undefined,
      /**
       * 検索テキスト変更Subject
       */
      textSubject: new Subject(),
      /**
       * 検索中
       */
      fetching: false,
      /**
       * 検索テキストでのヒットなし
       */
      noHits: false,
      /**
       * 検索した商品数
       */
      itemFoundCount: 0,
      /**
       * 選択肢に表示する商品
       */
      items: [],
      /**
       * 選択されている商品
       */
      selected: [],
    }
  },
  watch: {
    company: {
      immediate: true,
      async handler(company) {
        if (company) {
          // 企業が決まらないと分類は取得できない
          this.itemCategories = await this.$repositories
            .get('itemCategory')
            .get(company, this.posRestriction)
            .then(categories => [
              {
                code: undefined,
                name: '全分類',
                children: categories,
                open: true,
              },
            ])

          if (this.categories.length > 0) {
            const categoriesSet = new Set(this.categories)

            walk(this.itemCategories[0], 0, entry => {
              if (entry.children) {
                return
              }
              entry.state = categoriesSet.has(entry.code) ? 'checked' : 'none'
            })
          }
        }
      },
    },
    categories(c) {
      if (c.length === 0) {
        this.$emit('update:excludeOtherCategoryItems', false)
      }
    },
  },
  computed: {
    _excludeOtherCategoryItems: {
      get() {
        return this.excludeOtherCategoryItems
      },
      set(v) {
        this.$emit('update:excludeOtherCategoryItems', v)
      },
    },
    /**
     * 検索した結果が表示最大件数をオーバーしているか
     */
    itemFountExceedsLimit() {
      return this.itemFoundCount > this.maxSearchCount
    },
  },
  methods: {
    /**
     * 検索ワード変更
     */
    updateText(v) {
      this.text = v
      this.textSubject.next(v)
    },
  },
  mounted() {
    // エントリー名絞込のトリガーです
    // 絞込テキストの入力があるとfilterTextSubjectに通知をするので、
    // 適度なタイミング（xxx ms入力が止まっている and 前回と値が違う）で
    // 実際の絞り込み処理を行います。
    const subscription = this.textSubject
      .asObservable()
      .pipe(
        // TODO 棚割パターン検索はBigQueryで実行されるのでホイホイアクセス
        //      しない方がいいかも。
        debounceTime(1000),
        distinctUntilChanged(),
        tap(() => {
          this.fetching = true
        }),
        map(text => text.trim()),
        switchMap(text => {
          if (!text) {
            return of([])
          } else {
            return from(
              this.$repositories.get('tanawari').get({
                company: this.company,
                itemCategories: this.categories.join(','),
                dateFrom: this.dateFrom,
                dateTo: this.dateTo,
                keyword: text,
              }),
            )
          }
        }),
      )
      .subscribe(result => {
        this.noHits = result.length === 0
        this.itemFoundCount = result.length
        this.items = result.slice(0, this.maxSearchCount)
        this.selected = []
        this.fetching = false
      })

    this.$on('hook:beforeDestroy', () => subscription.unsubscribe())

    // 入力欄にフォーカスを自動で当てたいが、ダイアログ内にこのコンポーネントを置いた場合、
    // 少し待たないとfocusが効かなかった（$elではちゃんとinput要素が取れていたが）。
    setTimeout(() => {
      this.$refs.text.$el.focus()
    }, 500)
  },
}

/**
 * ツリーノードの再帰処理
 */
function walk(entry, level, f) {
  if (entry.children) {
    for (const child of entry.children) {
      walk(child, level + 1, f)
    }
  }
  f(entry, level)
}
</script>

<style lang="scss" scoped>
.items-container {
  height: 200px;
  overflow-x: hidden;
  overflow-y: auto;
  border: 1px solid var(--color-border);
  border-radius: 5px;
}

.keyword-search-wrapper {
  margin-top: var(--spacing-medium);
}

.keyword-search {
  width: 100%;
  display: flex;
  align-items: center;

  &__keyword-input {
    flex: 1;
  }
}

.message-max-count-exceeds {
  color: #ff7043; // TODO 色ちゃんとする
  width: 100%;
  text-align: center;
}

.fetching {
  background: rgba($color: #000000, $alpha: 0.2);
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 10;
}
</style>
